/**
 * Renderer shell-mode query contract (见 08 / 21).
 *
 * A window can load the same `main_window` entry in different shell modes.
 * Detached surfaces use `?surface=` (see `surface-target.ts`); the built-in
 * browser host uses `?shell=browser`. Writers and readers live together so the
 * query cannot drift across main / renderer / future hosts.
 */

/** Known top-level shell modes besides the default workbench. */
export type ShellMode = 'browser';

/** A shell target parsed from a renderer URL query. */
export interface ShellTarget {
  mode: ShellMode;
}

/**
 * Build the query that boots a non-workbench shell. Window geometry stays out
 * of the URL — that is a WindowManager concern.
 */
export function buildShellQuery(mode: ShellMode): Record<string, string> {
  return { shell: mode };
}

/**
 * Parse a shell mode from `location.search` (or a bare query string). Returns
 * `null` for the default workbench (no recognized `shell=`). Pure so it can be
 * unit-tested without a DOM.
 */
export function parseShellTarget(search: string): ShellTarget | null {
  const params = new URLSearchParams(search);
  const mode = params.get('shell');
  if (mode === 'browser') {
    return { mode };
  }
  return null;
}

/**
 * Parse a shell target out of a full page URL. Malformed hrefs yield `null`.
 */
export function parseShellTargetFromHref(href: string): ShellTarget | null {
  try {
    return parseShellTarget(new URL(href).search);
  } catch {
    return null;
  }
}
